import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'

dayjs.extend(customParseFormat)

/*
 | =============================================================================
 | Field mapping for home loan vault products
 |
 | Two payload shapes reach this mapper and they are not compatible:
 |
 |  - self-entered — captured by the vault funnel
 |    (app/constants/funnel/vault-home-loan.js)
 |  - purchase — synced from a settled Salesforce opportunity, flagged with
 |    is_purchase_history
 |
 | `loan_type` collides between them: it is the rate structure (Fixed / Variable)
 | in the funnel shape, but occupancy ("Owner Occupied") in the purchase shape,
 | where the rate structure lives on `rate_structure`. Reading it unbranched
 | renders occupancy under a "Loan Type" label and drops the rate entirely, so
 | the branch below is load-bearing.
 |
 | Both surfaces that render a home loan — the vault list card (via the vault
 | store getters) and the product details page — read from here, so their values
 | cannot drift apart.
 | =============================================================================
 */

/*
 | Lender display name, whichever shape it arrives in.
 |
 | The vault funnel stores a provider object; the Salesforce sync has sent the
 | lender both as a plain name string and as an object, so neither shape can be
 | assumed. Reading `.fund_name` off a string, or interpolating an object into a
 | template, both fail silently — as a blank field or a literal "[object Object]".
 | @param lender
 | @returns {string}
 */
export const lenderName = lender => {
  if (typeof lender === 'string') {
    return lender
  }
  return lender?.fund_name || lender?.name || ''
}

/*
 | Remaining term between now and the end of the loan, for self-entered loans
 | that record a start date and a term.
 | @param payload
 | @returns {string|null}
 */
const remainingTerm = payload => {
  const term = payload?.loan_term !== 'Other' ? payload?.loan_term?.replace(' year', '')?.trim() : null
  if (!term || !payload?.loan_start_date) {
    return null
  }
  const endDate = dayjs(payload.loan_start_date, 'DD/MM/YYYY').add(parseInt(term), 'year')
  if (!endDate.isValid()) {
    return null
  }
  const today = dayjs()
  const years = endDate.diff(today, 'year')
  const months = endDate.diff(today.add(years, 'year'), 'month')
  const parts = [years ? `${years} years` : '', months ? `${months} months` : '']
  return parts.join(' ').trim() || null
}

/*
 | Loan term as a readable duration. The purchase payload carries a bare number
 | of years ("15"); the funnel shape already includes the unit ("25 year").
 | @param loanTerm
 | @returns {string}
 */
export const loanTermText = loanTerm => {
  if (!loanTerm || loanTerm === 'Other') {
    return ''
  }
  const term = loanTerm.toString().trim()
  if (/^\d+$/.test(term)) {
    return `${term} ${parseInt(term) === 1 ? 'year' : 'years'}`
  }
  return term
}

/*
 | Fields for a home loan purchased through Compare Club and synced from
 | Salesforce.
 | @param payload
 | @returns {Array}
 */
const purchaseFields = payload => {
  const loanTerm = loanTermText(payload?.loan_term)
  const loanAmount = payload?.loan_amount
    ? `${formatCurrency(payload.loan_amount)}${loanTerm ? ` over ${loanTerm}` : ''}`
    : ''
  const rate = payload?.interest_rate
    ? `${appendPercentage(payload.interest_rate)}${payload?.rate_structure ? ` ${payload.rate_structure.toLowerCase()}` : ''}`
    : ''
  // A deposit is meaningless on a refinance, where Salesforce still populates
  // the field from the originating opportunity.
  const isRefinance = payload?.loan_purpose?.toLowerCase() === 'refinance'

  return [
    { name: 'lender', label: 'Lender', value: lenderName(payload?.lender) },
    { name: 'loanAmount', label: 'Loan Amount', value: loanAmount },
    { name: 'rate', label: 'Rate', value: rate },
    { name: 'propertyUse', label: 'Property Use', value: payload?.loan_type || '' },
    { name: 'repaymentType', label: 'Repayment Type', value: payload?.repayment_structure || '' },
    { name: 'loanPurpose', label: 'Loan Purpose', value: strToCapitalize(payload?.loan_purpose) || '' },
    { name: 'propertyValue', label: 'Property Value', value: payload?.property_value ? formatCurrency(payload.property_value) : '' },
    { name: 'deposit', label: 'Deposit', value: !isRefinance && payload?.deposit_amount ? formatCurrency(payload.deposit_amount) : '' },
    { name: 'propertyAddress', label: 'Property Address', value: payload?.property_address || '' },
    { name: 'reference', label: 'Compare Club Reference #', value: payload?.live_submit_id || '' }
  ]
}

/*
 | Fields for a home loan the member entered themselves through the vault funnel.
 |
 | The funnel is a single-page form now (loan type / loan start date / payment
 | type are no longer asked - see BASE_SINGLE_PAGE_ITEMS in
 | app/pages/dashboard/vault/[...product]/form/home-loan/step/[step].vue), so
 | only the fields it still collects are rendered here. `variable_rate` is read
 | as a fallback purely for loans entered while the single-page form collected a
 | variable rate, before it switched back to fixed.
 | @param payload
 | @returns {Array}
 */
const selfEnteredFields = payload => {
  const rate = payload?.fixed_rate
    ? appendPercentage(payload.fixed_rate)
    : (payload?.variable_rate ? appendPercentage(payload.variable_rate) : '')

  const term = remainingTerm(payload)
  const balance = payload?.current_balance
    ? `${formatCurrency(payload.current_balance)}${term ? ` over the next ${term}` : ''}`
    : ''

  const loanTerm = payload?.loan_term !== 'Other' ? payload?.loan_term : ''
  const loanAmount = payload?.loan_amount
    ? `${formatCurrency(payload.loan_amount)}${loanTerm ? ` over ${loanTerm}` : ''}`
    : ''

  return [
    { name: 'balance', label: ' Current Balance', value: balance },
    { name: 'loanAmount', label: 'Loan Amount', value: loanAmount },
    { name: 'rate', label: 'Current Interest Rate', value: rate },
    { name: 'paymentFrequency', label: 'Payment Frequency', value: payload?.payment_frequency || '' },
    { name: 'propertyType', label: 'Property Type', value: payload?.property_type || '' }
  ]
}

/*
 | Display fields for a home loan vault product, in render order. Fields with no
 | value are omitted rather than rendered as "N/A".
 | @param product - vault product, including is_purchase_history and payload
 | @returns {Array} [{ name, label, value, breakdown? }]
 */
export const homeLoanFields = product => {
  const payload = product?.payload
  if (!payload || !Object.keys(payload).length) {
    return []
  }
  const fields = product?.is_purchase_history ? purchaseFields(payload) : selfEnteredFields(payload)
  return fields.filter(field => !!field.value)
}

/*
 | Look up a single mapped field's value by name.
 | @param product
 | @param name
 | @returns {string}
 */
export const homeLoanFieldValue = (product, name) =>
  homeLoanFields(product).find(field => field.name === name)?.value || ''
